import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  FileText, Search, Filter, Trash2, Edit2, Copy, Bookmark,
  BookmarkCheck, Loader2, Instagram, Linkedin, Twitter, Youtube,
  MoreVertical, Layers, Video, CalendarDays, Clock, CheckCircle2, Eye,
  BookOpen, Megaphone, Mail, Send, RotateCw, Archive, Sparkles, X as XIcon,
  ArrowUpDown, ExternalLink, CheckSquare, Wand2, ScrollText,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { SchedulePostDialog } from '../components/SchedulePostDialog';
import { ViewPostDialog } from '../components/ViewPostDialog';
import { PostStatusBadge } from '../components/PostStatusBadge';
import { CampaignApproveDialog } from '../components/CampaignApproveDialog';
import { VideoStudioModal } from '../components/VideoStudioModal';
import { useFeature } from '../hooks/useFeatures';
import { SendForReviewDialog } from '../components/SendForReviewDialog';
import { SentReviewsTab } from '../components/SentReviewsTab';
import { SentPostsTab } from '../components/SentPostsTab';
import { ClientFilterPill, ClientBadge } from '../components/ClientFilterPill';
import { useClients } from '../hooks/useClients';
import { toast } from 'sonner';

export const PostsPage = () => {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignFilter = searchParams.get('campaign');
  // Top-level tab: 'posts' (default) or 'reviews'. Persisted in the URL so
  // the back button + bookmarking work, and so the Send-for-review dialog
  // can deep-link the operator to their tracking screen after sending.
  const activeTab = (() => {
    const t = searchParams.get('tab');
    if (t === 'reviews') return 'reviews';
    if (t === 'sent') return 'sent';
    return 'posts';
  })();
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'reviews') params.set('tab', 'reviews');
    else if (next === 'sent') params.set('tab', 'sent');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  };
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterSaved, setFilterSaved] = useState(false);
  const [filterClient, setFilterClient] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { clients, byId: clientsById } = useClients();
  const [editPost, setEditPost] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [viewPost, setViewPost] = useState(null);
  const [videoPost, setVideoPost] = useState(null);
  const [videoScript, setVideoScript] = useState(null);
  const videoFeature = useFeature('video_studio');
  const [total, setTotal] = useState(0);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showSendReview, setShowSendReview] = useState(false);

  const draftCount = posts.filter((p) => p.status === 'draft').length;
  const draftPlatforms = posts.filter((p) => p.status === 'draft').map((p) => p.platform);

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterPlatform !== 'all') params.append('platform', filterPlatform);
      if (filterType !== 'all') params.append('post_type', filterType);
      if (filterSaved) params.append('saved_only', 'true');
      if (campaignFilter) params.append('campaign_id', campaignFilter);
      if (filterClient && filterClient !== 'all') params.append('client_id', filterClient);
      params.append('limit', '200');

      const response = await api.get(`/posts?${params.toString()}`);
      setPosts(response.data.posts);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [api, filterPlatform, filterType, filterSaved, filterClient, campaignFilter]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterPlatform !== 'all') params.append('platform', filterPlatform);
    if (filterType !== 'all') params.append('post_type', filterType);
    if (filterSaved) params.append('saved_only', 'true');
    if (campaignFilter) params.append('campaign_id', campaignFilter);
    if (filterClient && filterClient !== 'all') params.append('client_id', filterClient);
    params.append('limit', '200');
    let active = true;
    api.get(`/posts?${params.toString()}`)
      .then((r) => { if (active) { setPosts(r.data.posts); setTotal(r.data.total); } })
      .catch(() => toast.error('Failed to load posts'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, filterPlatform, filterType, filterSaved, filterClient, campaignFilter]);

  const handleApproveAll = () => {
    if (!campaignFilter || draftCount === 0) return;
    setShowApproveDialog(true);
  };

  const clearCampaignFilter = () => {
    setSearchParams({});
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleArchive = async (post) => {
    try {
      await api.post(`/posts/${post.id}/archive`);
      // Remove from active feed; user can find it under /archive
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success('Moved to Archive');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    }
  };

  // ---- Multi-select helpers ----
  const toggleSelect = (postId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const visibleIds = filteredPosts.map((p) => p.id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const verb = action === 'delete' ? 'permanently delete' : 'archive';
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} post(s)? This cannot be undone.`)) return;
    try {
      const res = await api.post('/posts/bulk', { action, post_ids: ids });
      const n = res.data?.affected ?? ids.length;
      // Optimistic remove from active feed
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.id)));
      setTotal((prev) => Math.max(0, prev - n));
      exitSelectMode();
      toast.success(action === 'delete' ? `Deleted ${n} post(s)` : `Archived ${n} post(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${verb} posts`);
    }
  };

  const handleBulkReschedule = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Reschedule ${ids.length} post(s) at 4-hour spacing starting now? `
      + `Failed posts will be reset, scheduled posts will be re-timed. Published posts are skipped.`
    )) return;
    try {
      const res = await api.post('/posts/reschedule/bulk', {
        post_ids: ids,
        spacing_hours: 4,
        daily_start_hour: 6,
        daily_end_hour: 22,
      });
      const n = res.data?.rescheduled_count ?? 0;
      const last = res.data?.last_scheduled_for;
      toast.success(
        last
          ? `Rescheduled ${n} post(s). Last post: ${new Date(last).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
          : `Rescheduled ${n} post(s)`
      );
      exitSelectMode();
      // Refresh feed so new times show up
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reschedule failed');
    }
  };

  /** One-click "fix all failed posts" — re-schedules every `failed` post the
   * user owns at 4h spacing starting now. Useful when the queue has been
   * stuck on a global publishing bug. */
  const handleRescheduleAllFailed = async () => {
    if (!window.confirm(
      'Reschedule ALL failed posts at 4-hour spacing starting now? This re-times every post in your queue with status=failed.'
    )) return;
    try {
      const res = await api.post('/posts/reschedule/bulk', {
        filter_status: ['failed'],
        spacing_hours: 4,
        daily_start_hour: 6,
        daily_end_hour: 22,
      });
      const n = res.data?.rescheduled_count ?? 0;
      if (n === 0) {
        toast.message('No failed posts to reschedule.');
        return;
      }
      toast.success(`Rescheduled ${n} failed post(s). The publisher will pick them up.`);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reschedule failed');
    }
  };

  const handleToggleSave = async (post) => {
    try {
      await api.put(`/posts/${post.id}`, { is_saved: !post.is_saved });
      setPosts(posts.map(p => p.id === post.id ? { ...p, is_saved: !p.is_saved } : p));
      toast.success(post.is_saved ? 'Removed from saved' : 'Saved!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/posts/${editPost.id}`, {
        post_title: editPost.post_title,
        caption: editPost.caption,
        hashtags: editPost.hashtags,
        image_prompt: editPost.image_prompt
      });
      setPosts(posts.map(p => p.id === editPost.id ? editPost : p));
      setEditPost(null);
      toast.success('Post updated');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handlePublishNow = async (post) => {
    if (!window.confirm(`Publish this post to ${post.platform} right now?`)) return;
    const promise = api.post(`/posts/${post.id}/publish-now`).then((res) => {
      const updated = res.data;
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (updated.publish_status === 'published') return updated;
      throw new Error(updated.publish_error || 'Publish failed');
    });
    toast.promise(promise, {
      loading: `Publishing to ${post.platform}…`,
      success: 'Published!',
      error: (err) => err?.message || 'Publish failed',
    });
  };

  const handleRetryPublish = async (post) => {
    const promise = api.post(`/posts/${post.id}/retry-publish`).then((res) => {
      const updated = res.data;
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (updated.publish_status === 'published') return updated;
      throw new Error(updated.publish_error || 'Retry failed');
    });
    toast.promise(promise, {
      loading: 'Retrying…',
      success: 'Published!',
      error: (err) => err?.message || 'Retry failed',
    });
  };

  const handleRegenerateMedia = async (post) => {
    // Inline AI image regeneration for posts missing media — typically used
    // on failed-IG-publish posts where the original campaign-time image
    // generation was skipped or errored.
    const promise = api.post(`/posts/${post.id}/regenerate-media`).then((res) => {
      const updated = res.data;
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      return updated;
    });
    toast.promise(promise, {
      loading: 'Generating image…',
      success: 'Media attached. You can now publish.',
      error: (err) => err?.response?.data?.detail || 'Image generation failed',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      Instagram: <Instagram className="w-4 h-4" />,
      LinkedIn: <Linkedin className="w-4 h-4" />,
      TikTok: <span className="text-xs font-bold">TT</span>,
      'Bluesky': <Twitter className="w-4 h-4" />,
      Facebook: <span className="text-xs font-bold">FB</span>,
      'YouTube Shorts': <Youtube className="w-4 h-4" />
    };
    return icons[platform] || <FileText className="w-4 h-4" />;
  };

  const getTypeIcon = (type) => {
    const icons = {
      carousel: <Layers className="w-4 h-4" />,
      video_script: <Video className="w-4 h-4" />,
      standard: <FileText className="w-4 h-4" />,
      blog: <BookOpen className="w-4 h-4" />,
      ad: <Megaphone className="w-4 h-4" />,
      email: <Mail className="w-4 h-4" />
    };
    return icons[type] || icons.standard;
  };

  const filteredPosts = (() => {
    const matched = posts.filter(post =>
      post.post_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.caption?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const sorters = {
      created_desc: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      created_asc: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      scheduled_asc: (a, b) => {
        // Posts without a scheduled date sort to the end
        const da = a.scheduled_for ? new Date(a.scheduled_for).getTime() : Infinity;
        const db = b.scheduled_for ? new Date(b.scheduled_for).getTime() : Infinity;
        return da - db;
      },
      scheduled_desc: (a, b) => {
        const da = a.scheduled_for ? new Date(a.scheduled_for).getTime() : -Infinity;
        const db = b.scheduled_for ? new Date(b.scheduled_for).getTime() : -Infinity;
        return db - da;
      },
      status: (a, b) => (a.status || '').localeCompare(b.status || ''),
      platform: (a, b) => {
        // Primary: platform name (case-insensitive so "Facebook" and "facebook"
        // sort together). Secondary: status — scheduled posts first, then
        // drafts, then published / failed — so within each platform group the
        // user sees what's coming up before what's already shipped.
        const pa = (a.platform || '').toLowerCase();
        const pb = (b.platform || '').toLowerCase();
        if (pa !== pb) return pa.localeCompare(pb);
        const order = { scheduled: 0, draft: 1, failed: 2, published: 3 };
        const sa = order[a.status] ?? 4;
        const sb = order[b.status] ?? 4;
        if (sa !== sb) return sa - sb;
        // Tertiary: scheduled posts by upcoming date, others by recency
        if (a.status === 'scheduled' && b.status === 'scheduled') {
          return new Date(a.scheduled_for || 0) - new Date(b.scheduled_for || 0);
        }
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      },
    };
    return [...matched].sort(sorters[sortBy] || sorters.created_desc);
  })();

  return (
    <div className="space-y-6" data-testid="posts-page">
      {/* Campaign banner */}
      {campaignFilter && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 via-violet-50 to-pink-50" data-testid="campaign-banner">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-white border border-indigo-200 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-900">AI campaign view</p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  Showing <strong>{total}</strong> post{total === 1 ? '' : 's'} from this campaign — <strong>{draftCount}</strong> still in draft.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {draftCount > 0 && (
                <Button
                  onClick={handleApproveAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="approve-all-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve all & schedule ({draftCount})
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCampaignFilter}
                data-testid="clear-campaign-filter"
              >
                <XIcon className="w-3.5 h-3.5 mr-1" />
                Clear filter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">My Posts</h1>
          <p className="text-zinc-500">{total} posts {campaignFilter ? 'in this campaign' : 'generated'}</p>
        </div>
      </div>

      {/* Top-level tab switcher: post list ↔ sent client reviews tracking. */}
      <div className="border-b border-zinc-200">
        <nav className="flex gap-1 -mb-px" aria-label="Posts sections">
          <button
            type="button"
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'posts'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
            data-testid="posts-tab-posts"
          >
            <FileText className="w-4 h-4 mr-1.5 inline-block" />
            Posts
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'sent'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
            data-testid="posts-tab-sent"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5 inline-block" />
            Sent
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'reviews'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
            }`}
            data-testid="posts-tab-reviews"
          >
            <Send className="w-4 h-4 mr-1.5 inline-block" />
            Sent Reviews
          </button>
        </nav>
      </div>

      {activeTab === 'reviews' ? (
        <SentReviewsTab />
      ) : activeTab === 'sent' ? (
        <SentPostsTab />
      ) : (
      <>
      {/* Filters */}
      <Card className="border-zinc-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                <SelectTrigger className="w-[140px] flex-1 min-w-[130px] sm:flex-none" data-testid="filter-platform">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                  <SelectItem value="Bluesky">Bluesky</SelectItem>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="YouTube Shorts">YouTube Shorts</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[140px] flex-1 min-w-[130px] sm:flex-none" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="carousel">Carousel</SelectItem>
                  <SelectItem value="video_script">Video Script</SelectItem>
                  <SelectItem value="blog">Blog</SelectItem>
                  <SelectItem value="ad">Ad</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={filterSaved ? 'default' : 'outline'}
                className={filterSaved ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => setFilterSaved(!filterSaved)}
                data-testid="filter-saved-btn"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Saved
              </Button>
              <ClientFilterPill
                value={filterClient}
                onChange={setFilterClient}
                clients={clients}
              />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px] flex-1 min-w-[150px] sm:flex-none" data-testid="filter-sort">
                  <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500 mr-1" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_desc">Newest first</SelectItem>
                  <SelectItem value="created_asc">Oldest first</SelectItem>
                  <SelectItem value="scheduled_asc">Scheduled (soonest)</SelectItem>
                  <SelectItem value="scheduled_desc">Scheduled (latest)</SelectItem>
                  <SelectItem value="status">By status</SelectItem>
                  <SelectItem value="platform">By platform</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleRescheduleAllFailed}
                data-testid="reschedule-all-failed-btn"
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                title="Reschedule every failed post at 4-hour spacing starting now"
              >
                <RotateCw className="w-4 h-4 mr-2" />
                Fix failed posts
              </Button>
              <Button
                variant={selectMode ? 'default' : 'outline'}
                className={selectMode ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                data-testid="toggle-select-mode-btn"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectMode ? 'Done' : 'Select'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action toolbar — only when select mode is active */}
      {selectMode && (
        <Card className="border-indigo-200 bg-indigo-50/40" data-testid="bulk-actions-bar">
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={
                  filteredPosts.length > 0 &&
                  filteredPosts.every((p) => selectedIds.has(p.id))
                }
                onCheckedChange={toggleSelectAllVisible}
                data-testid="bulk-select-all"
              />
              <span className="text-sm font-medium text-zinc-700">
                {selectedIds.size === 0
                  ? `Select posts to delete or archive`
                  : `${selectedIds.size} selected`}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkReschedule}
                disabled={selectedIds.size === 0}
                data-testid="bulk-reschedule-btn"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <Clock className="w-4 h-4 mr-2" />
                Reschedule (4h spacing)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSendReview(true)}
                disabled={selectedIds.size === 0}
                data-testid="bulk-send-review-btn"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <Send className="w-4 h-4 mr-2" />
                Send for review ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('archive')}
                disabled={selectedIds.size === 0}
                data-testid="bulk-archive-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={() => handleBulkAction('delete')}
                disabled={selectedIds.size === 0}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedIds.size})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <Card className="border-zinc-200">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">No posts found</p>
            <p className="text-zinc-400 text-sm mt-1">Try adjusting your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPosts.map((post) => {
            const isSelected = selectedIds.has(post.id);
            const handleCardClick = () => {
              if (selectMode) {
                toggleSelect(post.id);
              } else {
                setViewPost(post);
              }
            };
            return (
            <Card
              key={post.id}
              className={`border-zinc-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer ${
                isSelected ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50/30' : ''
              }`}
              onClick={handleCardClick}
              data-testid={`post-card-${post.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {selectMode && (
                      <div onClick={(e) => e.stopPropagation()} className="mr-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(post.id)}
                          data-testid={`select-post-${post.id}`}
                        />
                      </div>
                    )}
                    <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center">
                      {getPlatformIcon(post.platform)}
                    </div>
                    <div>
                      <span className="text-xs text-zinc-500">{post.platform}</span>
                      <div className="flex items-center gap-1">
                        {getTypeIcon(post.post_type)}
                        <span className="text-xs text-zinc-400 capitalize">{post.post_type?.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => setViewPost(post)} data-testid={`view-post-menu-${post.id}`}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditPost(post)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {!videoFeature.locked && (
                        <DropdownMenuItem onClick={() => { setVideoScript(null); setVideoPost(post); }} data-testid={`video-studio-${post.id}`}>
                          <Video className="w-4 h-4 mr-2" />
                          Video Studio
                        </DropdownMenuItem>
                      )}
                      {!videoFeature.locked && (
                        <DropdownMenuItem onClick={() => { setVideoScript(`${post.post_title || ''}\n\n${post.caption || ''}`.trim()); setVideoPost(post); }} data-testid={`record-teleprompter-${post.id}`}>
                          <ScrollText className="w-4 h-4 mr-2" />
                          Record video (teleprompter)
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => setScheduleTarget(post)}
                        data-testid={`schedule-post-${post.id}`}
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {post.scheduled_for ? 'Reschedule' : 'Schedule'}
                      </DropdownMenuItem>
                      {post.status !== 'published' && (
                        <DropdownMenuItem
                          onClick={() => handlePublishNow(post)}
                          data-testid={`publish-now-${post.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Publish now
                        </DropdownMenuItem>
                      )}
                      {post.status === 'failed' && (
                        <DropdownMenuItem
                          onClick={() => handleRetryPublish(post)}
                          data-testid={`retry-publish-${post.id}`}
                        >
                          <RotateCw className="w-4 h-4 mr-2" />
                          Retry publish
                        </DropdownMenuItem>
                      )}
                      {(post.media || []).length === 0 && (
                        <DropdownMenuItem
                          onClick={() => handleRegenerateMedia(post)}
                          data-testid={`regenerate-media-${post.id}`}
                        >
                          <Wand2 className="w-4 h-4 mr-2" />
                          Regenerate media
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => copyToClipboard(`${post.post_title}\n\n${post.caption}\n\n${post.hashtags}`)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleSave(post)}>
                        {post.is_saved ? (
                          <>
                            <BookmarkCheck className="w-4 h-4 mr-2" />
                            Unsave
                          </>
                        ) : (
                          <>
                            <Bookmark className="w-4 h-4 mr-2" />
                            Save
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleArchive(post)}
                        data-testid={`archive-post-${post.id}`}
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(post.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <h3 className="font-semibold text-zinc-900 mb-2 line-clamp-2">{post.post_title}</h3>
                <p className="text-sm text-zinc-600 mb-3 line-clamp-3">{post.caption}</p>

                {post.client_id && (
                  <div className="mb-2">
                    <ClientBadge clientId={post.client_id} clientsById={clientsById} />
                  </div>
                )}

                {(post.status === 'scheduled' || post.status === 'published' || post.status === 'failed') && (
                  <PostStatusBadge post={post} className="mb-2" />
                )}

                <div className="flex flex-wrap gap-1 mb-3">
                  {post.hashtags?.split(' ').filter(Boolean).slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                  {post.hashtags?.split(' ').filter(Boolean).length > 3 && (
                    <span className="text-xs text-zinc-400">+{post.hashtags.split(' ').filter(Boolean).length - 3}</span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
                  <span className="text-xs text-zinc-400">
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-3">
                    {post.published_url && (
                      <a
                        href={post.published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                        data-testid={`view-live-${post.id}`}
                      >
                        View live
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {post.is_saved && (
                      <BookmarkCheck className="w-4 h-4 text-indigo-600" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editPost} onOpenChange={() => setEditPost(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          {editPost && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editPost.post_title}
                  onChange={(e) => setEditPost({ ...editPost, post_title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Caption</Label>
                <Textarea
                  value={editPost.caption}
                  onChange={(e) => setEditPost({ ...editPost, caption: e.target.value })}
                  rows={4}
                  data-testid="edit-caption-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hashtags</Label>
                <Input
                  value={editPost.hashtags}
                  onChange={(e) => setEditPost({ ...editPost, hashtags: e.target.value })}
                  data-testid="edit-hashtags-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image Prompt</Label>
                <Textarea
                  value={editPost.image_prompt}
                  onChange={(e) => setEditPost({ ...editPost, image_prompt: e.target.value })}
                  rows={3}
                  data-testid="edit-image-prompt-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPost(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdate} data-testid="save-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SchedulePostDialog
        post={scheduleTarget}
        open={!!scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onScheduled={(updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))}
      />

      <ViewPostDialog
        post={viewPost}
        open={!!viewPost}
        onClose={() => setViewPost(null)}
        onEdit={(p) => { setViewPost(null); setEditPost(p); }}
        onSchedule={(p) => { setViewPost(null); setScheduleTarget(p); }}
      />

      <CampaignApproveDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        campaignId={campaignFilter}
        draftCount={draftCount}
        draftPlatforms={draftPlatforms}
        onDone={fetchPosts}
      />

      <SendForReviewDialog
        open={showSendReview}
        onOpenChange={setShowSendReview}
        postIds={Array.from(selectedIds)}
        onSent={() => { setSelectedIds(new Set()); fetchPosts(); }}
      />
      <VideoStudioModal
        post={videoPost}
        initialScript={videoScript}
        onClose={() => { setVideoPost(null); setVideoScript(null); }}
        onAttached={fetchPosts}
      />
    </div>
  );
};
